import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { formatApiErrorDetail } from '../utils/api';
import { LogIn, Mail, CheckCircle, KeyRound } from 'lucide-react';
import { BackToHome } from '../components/BackToHome';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, requestMagicLink } = useAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState('magic'); // 'magic' | 'password'
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await login(formData.email, formData.password);
      if (userData.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/account');
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMagicSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await requestMagicLink(formData.email);
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex flex-col p-4">
      <LanguageSwitcher variant="floating" />
      <BackToHome />
      <div className="flex-1 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8 max-w-md w-full">
          {/* Magic link mode */}
          {mode === 'magic' && !sent && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-amber-600" />
                </div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2 font-['Outfit']">{t('login.magic.heading')}</h1>
                <p className="text-slate-600 font-['Manrope']">{t('login.magic.subtitle')}</p>
              </div>

              <form data-testid="magic-link-form" onSubmit={handleMagicSubmit} className="space-y-6">
                {error && (
                  <div data-testid="login-error" className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="magic-email" className="block text-sm font-medium text-slate-700 mb-2">{t('login.email')}</label>
                  <input
                    data-testid="magic-email-input"
                    type="email"
                    id="magic-email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all duration-200"
                    placeholder="you@example.com"
                  />
                </div>

                <button
                  data-testid="magic-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-4 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 bg-[length:200%_100%] text-white rounded-full font-bold hover:bg-[position:100%_0] transition-all duration-500 shadow-lg shadow-amber-500/40 hover:shadow-2xl hover:shadow-amber-500/50 disabled:opacity-50"
                >
                  {loading ? t('login.magic.sending') : t('login.magic.submit')}
                </button>

                <div className="text-center space-y-3 pt-2">
                  <button
                    type="button"
                    data-testid="switch-to-password"
                    onClick={() => { setMode('password'); setError(''); }}
                    className="block w-full text-sm text-slate-500 hover:text-slate-800 font-medium"
                  >
                    {t('login.admin_toggle')}
                  </button>
                  <p className="text-sm text-slate-600">
                    {t('login.no_account')}{' '}
                    <Link to="/signup" data-testid="link-to-signup" className="text-blue-600 hover:text-blue-700 font-medium">
                      {t('login.become_supporter')}
                    </Link>
                  </p>
                </div>
              </form>
            </>
          )}

          {/* After magic link request — confirmation */}
          {mode === 'magic' && sent && (
            <div data-testid="magic-sent-confirm" className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3 font-['Outfit']">{t('login.magic.sent_title')}</h1>
              <p className="text-slate-600 mb-6 leading-relaxed">{t('login.magic.sent_body')}</p>
              <p className="text-xs text-slate-400 mb-6">{formData.email}</p>
              <button
                data-testid="magic-resend-btn"
                onClick={() => { setSent(false); setFormData({ ...formData, email: '' }); }}
                className="text-sm text-amber-700 hover:text-amber-900 font-medium"
              >
                {t('login.magic.resend')}
              </button>
            </div>
          )}

          {/* Password mode — admin fallback */}
          {mode === 'password' && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="w-8 h-8 text-blue-600" />
                </div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2 font-['Outfit']">{t('login.title')}</h1>
                <p className="text-slate-600 font-['Manrope']">{t('login.subtitle')}</p>
              </div>

              <form data-testid="login-form" onSubmit={handlePasswordSubmit} className="space-y-6">
                {error && (
                  <div data-testid="login-error" className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">{t('login.email')}</label>
                  <input
                    data-testid="login-email-input"
                    type="email" id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                    placeholder="you@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">{t('login.password')}</label>
                  <input
                    data-testid="login-password-input"
                    type="password" id="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-xl border-slate-200 bg-slate-50 focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                </div>

                <button
                  data-testid="login-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-full font-medium hover:bg-blue-700 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50"
                >
                  {loading ? t('login.submitting') : t('login.submit')}
                </button>

                <div className="text-center space-y-2">
                  <button
                    type="button"
                    data-testid="switch-to-magic"
                    onClick={() => { setMode('magic'); setError(''); }}
                    className="block w-full text-sm text-amber-700 hover:text-amber-900 font-medium"
                  >
                    {t('login.magic_toggle')}
                  </button>
                  <Link to="/forgot-password" data-testid="link-forgot-password" className="block text-sm text-slate-500 hover:text-blue-600 font-medium">
                    {t('login.forgot_password')}
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
